import { Skeleton } from '@/components/Skeleton';
import Template from '@/components/Template';
import { faRefresh } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DashboardType, DashboardVariable, OOBInfo } from '@squaredup/dashboards';
import { Serialised } from '@squaredup/ids';
import { defaultTimeframeEnum, UnresolvedTimeframe } from '@squaredup/timeframes';
import Button from 'components/button/Button';
import { useLinkedPluginConfigs } from 'components/hooks/useLinkedPluginConfigs';
import LoadingSpinner from 'components/LoadingSpinner';
import { useAppContext } from 'contexts/AppContext';
import DashboardContext from 'contexts/DashboardContext';
import { useDashboardForceRefetch } from 'dashboard-engine/hooks/useDashboardForceRefetch';
import RenderDynamic from 'dashboard-engine/render/RenderDynamic';
import { checkIfAllTilesUseFixedTimeframe } from 'dashboard-engine/util/dashboard';
import { usePageTitle } from 'lib/usePageTitle';
import { noop } from 'lodash';
import { NotFoundPanel } from 'pages/components/Panels/NotFoundPanel';
import { DashboardVariablesControl } from 'pages/dashboard/components/DashboardVariablesControl';
import { DashboardMetricsProvider } from 'pages/dashboard/DashboardMetrics';
import { getComparableDashboardString } from 'pages/dashboard/DashboardVersioning';
import { OobDashboardImportStatusModal, useOobDashboardImportStatus } from 'pages/dashboard/OobDashboardImportStatus';
import PluginIcon from 'pages/scope/PluginIcon';
import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCachedCurrentWorkspace } from 'services/WorkspaceUtil';
import { NavLink } from 'ui/nav/components/NavLink';
import DashboardTimeframeChooser from 'ui/timeframe/DashboardTimeframeChooser';
import { useZendeskLabels } from 'ui/zendesk/ZendeskContext';
import { CopyToWorkspaceModal } from './components/CopyToWorkspaceModal';
import { useCreateOobDashboard } from './components/useCreateOobDashboard';
import { useOobDashboard } from './components/useOobDashboard';
import { PluginConfig } from './Datasource';

export const DatasourceDashboard = () => {
    const { id: configId, '*': dashboardPath } = useParams();

    const { currentWorkspaceID: workspaceId } = useAppContext();

    const { data: linkedPlugins = [] } = useLinkedPluginConfigs(workspaceId, {
        refetchInterval: false
    });

    const linkedPlugin = linkedPlugins.find((v) => v.id === configId);

    const pluginDetails = linkedPlugin?.plugin as Serialised<PluginConfig> | undefined;

    const {
        data,
        isLoading: isLoadingDashboard,
        isFetched
    } = useOobDashboard(pluginDetails?.pluginId!, configId!, workspaceId!, dashboardPath!);

    if (isLoadingDashboard && dashboardPath !== undefined) {
        // show skeleton of header
        return (
            <div className='pt-[20px] flex gap-x-4 items-center'>
                <Skeleton className='max-w-[400px] flex-1 h-button' />
            </div>
        );
    }

    if (isFetched && !data && !isLoadingDashboard && dashboardPath) {
        return (
            <div className='flex flex-col items-center justify-center mt-48'>
                <div className='p-10 mx-auto text-center rounded-md text-textPrimary'>
                    {!isLoadingDashboard && dashboardPath && (
                        <NotFoundPanel
                            title='Dashboard not accessible'
                            body='The dashboard does not exist or you do not have permissions to access it.'
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div>
            {data && pluginDetails && configId && dashboardPath && (
                <DashboardMetricsProvider
                    key={data.dashboard.id}
                    dashboardContents={data.dashboard.content?.contents ?? []}
                    oobInfo={data.dashboard.oobInfo as OOBInfo}
                >
                    <Dashboard
                        dashboard={data.dashboard}
                        variables={data.variables}
                        pluginDetails={pluginDetails}
                        configName={linkedPlugin?.displayName}
                        pluginId={pluginDetails?.pluginId!}
                        configId={configId}
                        dashboardPath={dashboardPath}
                    />
                </DashboardMetricsProvider>
            )}
        </div>
    );
};

interface DashboardProps {
    dashboard: DashboardType;
    variables: DashboardVariable[];
    pluginDetails: Serialised<PluginConfig>;
    configName?: string;
    pluginId: string;
    configId: string;
    dashboardPath: string;
}

const Dashboard: React.FC<DashboardProps> = ({
    dashboard,
    variables: initialVariables,
    pluginDetails,
    pluginId,
    configName,
    configId,
    dashboardPath
}) => {
    const oobDashboardPluginImportStatus = useOobDashboardImportStatus({
        configId: dashboard.oobInfo?.configId,
        workspaceId: dashboard.workspaceId
    });

    const [variables, setVariables] = useState<DashboardVariable[]>(initialVariables);
    const [timeframe, setTimeframe] = useState<UnresolvedTimeframe>(dashboard.timeframe || defaultTimeframeEnum);
    const [copyModalOpen, setCopyModalOpen] = useState(false);

    const { isNarrowLayout } = useAppContext();

    const allTilesUseFixedTimeframe = checkIfAllTilesUseFixedTimeframe(dashboard.content?.contents);

    const { createDashboard, isCreatingDashboard } = useCreateOobDashboard(pluginId, configId, dashboardPath);

    /* If we have a dashboard but no content we know we're using the initialData 
        from the cache and therefore are fetching the dashboard content */
    const isLoaded = Boolean(dashboard.content) && !oobDashboardPluginImportStatus.isLoading;

    useZendeskLabels('dashboard');
    usePageTitle(dashboard.displayName);

    const currentWorkspace = useCachedCurrentWorkspace();

    const DashboardProviderValue = useMemo(
        () => ({
            editing: false,
            setEditing: undefined,
            dashboard,
            timeframe,
            currentWorkspace,
            showDetailedErrors: false,
            variables,
            setVariables,
            highlightedTile: undefined,
            showCopyTileAction: true,
            setHighlightedTile: noop,
            setPerformCompactionHandler: noop,
            updateTile: noop
        }),
        [dashboard, timeframe, variables, currentWorkspace]
    );

    const dashboardDiffString = getComparableDashboardString(dashboard);

    const triggerRefetch = useDashboardForceRefetch(dashboard);

    const MemoedRenderDynamic = useMemo(
        () => (
            <RenderDynamic
                config={dashboard.content}
                key={dashboard.id}
                isLoading={!oobDashboardPluginImportStatus.isImported}
            />
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dashboardDiffString, dashboard.id]
    );

    return (
        <DashboardContext.Provider value={DashboardProviderValue}>
            <Template
                key={dashboard.id}
                title={
                    <>
                        <span className='text-textSecondary'>
                            <NavLink className='inline text-textSecondary pb-0' to={`/datasource/${configId}`}>
                                {configName}
                            </NavLink>{' '}
                            /
                        </span>{' '}
                        {dashboard.displayName}
                    </>
                }
                icon={
                    <PluginIcon
                        pluginName={pluginDetails?.name || ''}
                        pluginId={pluginDetails?.basePlugin ? pluginDetails.pluginId : undefined}
                        className='w-10 h-10 mr-4'
                    />
                }
                postTitleActions={
                    <DashboardVariablesControl
                        canEdit={false}
                        disabled={!oobDashboardPluginImportStatus.isImportConfirmed}
                        shouldLoadVariableObjects={oobDashboardPluginImportStatus.isImported}
                    />
                }
                iconAlignment='center'
                iconWrapperClassName='mr-0'
                actions={
                    isLoaded && (
                        <div className='flex space-x-4'>
                            <Button
                                variant='secondary'
                                className='px-3'
                                icon={<FontAwesomeIcon icon={faRefresh} fixedWidth />}
                                onClick={triggerRefetch}
                            />
                            {!allTilesUseFixedTimeframe && (
                                <DashboardTimeframeChooser
                                    currentTimeframe={timeframe}
                                    defaultTimeframe={dashboard.timeframe || defaultTimeframeEnum}
                                    disabled={false}
                                    canChangeTimeframe={false}
                                    onCurrentTimeframeChange={setTimeframe}
                                />
                            )}
                            <Button
                                variant='secondary'
                                disabled={isCreatingDashboard}
                                onClick={() => setCopyModalOpen(true)}
                            >
                                {isCreatingDashboard && <LoadingSpinner size={18} className='mr-2' />}
                                Copy to workspace
                            </Button>
                        </div>
                    )
                }
                sticky
                equalPadding
                isNarrowLayout={isNarrowLayout}
            >
                {copyModalOpen && (
                    <CopyToWorkspaceModal
                        onCopy={() => createDashboard()}
                        isCopying={isCreatingDashboard}
                        close={() => setCopyModalOpen(false)}
                    />
                )}
                {isLoaded && (
                    <>
                        <div className='relative pb-4 mt-px'>
                            {oobDashboardPluginImportStatus.plugin &&
                                !oobDashboardPluginImportStatus.isImportConfirmed && (
                                    <OobDashboardImportStatusModal
                                        plugin={oobDashboardPluginImportStatus.plugin}
                                        isImportConfirmed={oobDashboardPluginImportStatus.isImportConfirmed}
                                        importStatus={oobDashboardPluginImportStatus.importStatus}
                                        setIsImportConfirmed={oobDashboardPluginImportStatus.setIsImportConfirmed}
                                    />
                                )}
                            {MemoedRenderDynamic}
                        </div>
                    </>
                )}
            </Template>
        </DashboardContext.Provider>
    );
};
